import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState({ logo_url: "/trmd-icon.png", site_logo: "/trmd-logo.webp", brand_name: "TRMD Performance", flashMaintenance: null });

  const applyRes = (data) => setSettings({
    logo_url: data.logo_url || "",
    brand_name: data.brand_name || "TRMD Performance",
    site_logo: data.site_logo || "/trmd-logo.webp",
    flashMaintenance: data.flashMaintenance || null,
    social: data.social || {},
  });

  const loadSettings = useCallback(async () => {
    try {
      const res = await api.get("/settings");
      applyRes(res.data);
    } catch {}
  }, []);

  const updateSettings = async (payload) => {
    const res = await api.put("/settings", payload);
    applyRes(res.data);
    return res.data;
  };

  useEffect(() => {
    loadSettings();
    const id = setInterval(loadSettings, 15000);
    return () => clearInterval(id);
  }, [loadSettings]);

  const checkAuth = useCallback(async () => {
    try {
      const res = await api.get("/auth/me");
      setUser(res.data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  // Email + password login (JWT cookie). Returns the user on success.
  const loginPw = async (email, password) => {
    const res = await api.post("/auth/login", { email, password });
    setUser(res.data);
    return res.data;
  };

  // Self-registration — account is created "pending" until an admin approves.
  const register = async (payload) => {
    const res = await api.post("/auth/register", payload);
    return res.data;
  };

  // Admin logs in with Emergent-managed Google Auth (admin email only).
  const loginGoogle = () => {
    const redirect = `${window.location.origin}/dashboard`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirect)}`;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    setUser(null);
    window.location.href = "/login";
  };

  const isAdmin = !!user?.is_admin;

  return (
    <AuthContext.Provider value={{ user, setUser, loading, loginPw, register, loginGoogle, logout, isAdmin, checkAuth, settings, updateSettings }}>
      {children}
    </AuthContext.Provider>
  );
}
